import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { ICampaignConfiguration } from '@forwardflow/types/onboarding'
import { initialCampaignConfiguration } from '@forwardflow/constants/onboarding.constants'

interface ICampaignStore {
   campaignConfiguration: ICampaignConfiguration
   isCampaignConfigured: boolean
   setCampaignConfiguration: (
      campaignConfiguration: Partial<ICampaignConfiguration>
   ) => void
   setIsCampaignConfigured: (isCampaignConfigured: boolean) => void
   getFirstFilterCriteria: () => {
      field: string
      value: string[]
      logic: 'AND'
      operator: 'CONTAINS_ANY' | 'CONTAINS_ALL' | 'NOT_CONTAINS_ANY'
   } | null
   getRestFilterCriterias: () => {
      field: string
      value: string[]
      logic: 'AND'
      operator: 'CONTAINS_ANY' | 'CONTAINS_ALL' | 'NOT_CONTAINS_ANY'
   }[]
   setFirstFilterCriteria: (firstFilterCriteria: {
      field: string
      value: string[]
      logic: 'AND'
      operator: 'CONTAINS_ANY' | 'CONTAINS_ALL' | 'NOT_CONTAINS_ANY'
   }) => void
   resetCampaignConfiguration: () => void
}

export const useCampaignStore = create<ICampaignStore>()(
   devtools((set, get) => ({
      campaignConfiguration: initialCampaignConfiguration,
      isCampaignConfigured: false,

      setCampaignConfiguration: (
         campaignConfiguration: Partial<ICampaignConfiguration>
      ) =>
         set((state) => ({
            campaignConfiguration: {
               ...state.campaignConfiguration,
               ...campaignConfiguration,
            },
         })),

      setIsCampaignConfigured: (isCampaignConfigured: boolean) =>
         set({ isCampaignConfigured }),

      getFirstFilterCriteria: () => {
         const { campaignConfiguration } = get()
         const criteria =
            campaignConfiguration?.campaign_filters?.eligible_criteria?.[0]
         if (!criteria) return null
         // Ensure the value is always a string[]
         return {
            ...criteria,
            value: Array.isArray(criteria.value)
               ? criteria.value.map(String)
               : [],
         }
      },

      getRestFilterCriterias: () => {
         const { campaignConfiguration } = get()
         const criterias =
            campaignConfiguration?.campaign_filters?.eligible_criteria?.slice(1)
         if (!criterias) return []
         return criterias.map((criteria) => ({
            ...criteria,
            value: Array.isArray(criteria.value)
               ? criteria.value.map(String)
               : [],
         }))
      },

      setFirstFilterCriteria: (firstFilterCriteria: {
         field: string
         value: string[]
         logic: 'AND'
         operator: 'CONTAINS_ANY' | 'CONTAINS_ALL' | 'NOT_CONTAINS_ANY'
      }) =>
         set((state) => ({
            campaignConfiguration: {
               ...state.campaignConfiguration,
               campaign_filters: {
                  ...state.campaignConfiguration?.campaign_filters,
                  eligible_criteria: [
                     {
                        field: firstFilterCriteria.field as
                           | 'source'
                           | 'stage'
                           | 'tags'
                           | 'assigned_pond_id'
                           | 'inquired_property',
                        value: firstFilterCriteria.value,
                        logic: firstFilterCriteria.logic,
                        operator: firstFilterCriteria.operator,
                     },
                     ...(state.campaignConfiguration?.campaign_filters?.eligible_criteria?.slice(
                        1
                     ) ?? []),
                  ],
               },
            },
         })),

      resetCampaignConfiguration: () =>
         set({
            campaignConfiguration: initialCampaignConfiguration,
            isCampaignConfigured: false,
         }),
   }))
)
